"use client";

import React, { useEffect, useState } from "react";
import { Blocks, Cpu, CircuitBoard, Sparkles, Code as Code2, Terminal, Bot, Trophy, Award, Cog, Box, Laptop, Search, Microchip, Network, Rocket, GraduationCap, BookOpen, Library, Globe, ChevronLeft, ChevronRight } from "lucide-react";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { Autoplay, Navigation, A11y } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import "swiper/css/a11y";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
interface EcosystemItem {
  name: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const ECOSYSTEM_ITEMS: EcosystemItem[] = [{
  name: "LEGO Education",
  icon: Blocks
}, {
  name: "Arduino",
  icon: Cpu
}, {
  name: "Raspberry Pi Foundation",
  icon: CircuitBoard
}, {
  name: "micro:bit Foundation",
  icon: Sparkles
}, {
  name: "Scratch (MIT Media Lab)",
  icon: Code2
}, {
  name: "Code.org",
  icon: Terminal
}, {
  name: "VEX Robotics",
  icon: Bot
}, {
  name: "FIRST Robotics",
  icon: Trophy
}, {
  name: "World Robot Olympiad (WRO)",
  icon: Award
}, {
  name: "Makeblock",
  icon: Cog
}, {
  name: "Tinkercad (Autodesk)",
  icon: Box
}, {
  name: "Microsoft Education",
  icon: Laptop
}, {
  name: "Google for Education",
  icon: Search
}, {
  name: "Intel",
  icon: Microchip
}, {
  name: "IBM",
  icon: Network
}, {
  name: "NASA STEM",
  icon: Rocket
}, {
  name: "Khan Academy",
  icon: GraduationCap
}, {
  name: "Coursera",
  icon: BookOpen
}, {
  name: "edX",
  icon: Library
}, {
  name: "UNESCO",
  icon: Globe
}];
const BRAND_THEMES = [{
  badge: "border-primary/20 bg-primary/10 text-primary group-hover:border-primary/40 group-hover:bg-primary/15",
  border: "hover:border-primary/40"
}, {
  badge: "border-accent/30 bg-accent/10 text-accent group-hover:border-accent/50 group-hover:bg-accent/20",
  border: "hover:border-accent/40"
}, {
  badge: "border-secondary/30 bg-secondary/10 text-secondary group-hover:border-secondary/50 group-hover:bg-secondary/20",
  border: "hover:border-secondary/40"
}];
function SliderNavigationControls() {
  const swiper = useSwiper();
  return <div className="mt-8 hidden items-center justify-center gap-3 sm:flex">
      <Button variant="outline" size="icon" className="h-10 w-10 rounded-lg border-border bg-card text-foreground transition-all duration-200 hover:border-primary/50 hover:bg-primary/10 hover:text-primary active:scale-95" aria-label="Previous ecosystem organization" onClick={() => swiper.slidePrev()}>
        <ChevronLeft className="h-5 w-5" />
      </Button>
      <Button variant="outline" size="icon" className="h-10 w-10 rounded-lg border-border bg-card text-foreground transition-all duration-200 hover:border-primary/50 hover:bg-primary/10 hover:text-primary active:scale-95" aria-label="Next ecosystem organization" onClick={() => swiper.slideNext()}>
        <ChevronRight className="h-5 w-5" />
      </Button>
    </div>;
}
export default function GlobalStemEcosystem() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const handleLangChange = () => {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      } else {
        setLang("vi");
      }
    };
    handleLangChange();
    window.addEventListener("languageChanged", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const handleMotionChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", handleMotionChange);
    return () => {
      window.removeEventListener("languageChanged", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
      mq.removeEventListener("change", handleMotionChange);
    };
  }, []);
  const titles = {
    vi: {
      heading: "Hệ sinh thái Giáo dục STEM & Robotics toàn cầu",
      subheading: "Những tên tuổi tiêu biểu trong ngành giáo dục STEM và Robotics toàn cầu, nơi học viên ROBOTO tiếp cận tư duy và công nghệ liên quan qua chương trình học"
    },
    en: {
      heading: "Global STEM & Robotics Education Ecosystem",
      subheading: "Leading names in global STEM and Robotics education, where ROBOTO students access related thinking and technology through the learning program"
    }
  };
  const currentCopy = titles[lang] || titles.vi;
  return <section id="globalstemecosystem" data-nav="light" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block with Technical Precision Rail */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="mx-auto mb-4 flex items-center justify-center gap-2">
            <span className="h-[2px] w-8 rounded-full bg-primary" />
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            <span className="h-[2px] w-8 rounded-full bg-secondary" />
          </div>

          <h2 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {currentCopy.heading}
          </h2>

          <p className="font-sans mx-auto mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
            {currentCopy.subheading}
          </p>
        </div>

        {/* 20 Organizations Swiper Slider */}
        <div className="mt-12">
          <Swiper modules={[Autoplay, Navigation, A11y]} loop={true} speed={600} autoplay={reduceMotion ? false : {
          delay: 2400,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }} slidesPerView={1.4} spaceBetween={14} breakpoints={{
          480: {
            slidesPerView: 2.2,
            spaceBetween: 16
          },
          640: {
            slidesPerView: 3,
            spaceBetween: 18
          },
          1024: {
            slidesPerView: 4,
            spaceBetween: 20
          },
          1280: {
            slidesPerView: 5,
            spaceBetween: 24
          }
        }} a11y={{
          prevSlideMessage: "Previous ecosystem organization",
          nextSlideMessage: "Next ecosystem organization"
        }} className="w-full !py-2">
            {ECOSYSTEM_ITEMS.map((item, index) => {
            const theme = BRAND_THEMES[index % BRAND_THEMES.length];
            const Icon = item.icon;
            return <SwiperSlide key={item.name} data-index={index} className="!h-auto">
                  <Card className={`group relative flex h-full min-h-[175px] flex-col items-center justify-center rounded-xl border border-border bg-card p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${theme.border}`}>
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border transition-all duration-300 ${theme.badge}`}>
                      <Icon className="h-7 w-7 transition-transform duration-300 group-hover:scale-110" />
                    </div>

                    <div className="mt-4 flex flex-1 items-center justify-center">
                      <h3 className="font-sans line-clamp-2 text-sm font-semibold tracking-tight text-card-foreground sm:text-base">
                        {item.name}
                      </h3>
                    </div>
                  </Card>
                </SwiperSlide>;
          })}

            <SliderNavigationControls />
          </Swiper>
        </div>
      </div>
    </section>;
}